import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAccount, useMsal } from '@azure/msal-react';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import UserDataService from '../services/localStorageService';
import execute from '../hooks/execute';
import { protectedResources } from '../config/msalConfig';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
 
  const { instance } = useMsal();
  let account;

  if (instance) {
      account = instance.getActiveAccount();
      console.log(instance.getAllAccounts());
  }



  const [userDetails, setUserDetails] = useState({});
  const [assignedPlan, setAssignedPlan] = useState(null);
  const [enabledFeatureKeys, setEnabledFeatureKeys] = useState([]);
  const [featureAccessLoaded, setFeatureAccessLoaded] = useState(false);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
          if(userDetails.id === account.localAccountId){
            return;
          }
          //console.log('fetchUserDetails from UserContext', account.idTokenClaims.email);
          const response = await execute("GET", `${protectedResources.api.baseEndPoint}/customerUsers/currentUserData`);
          if (response && response.success && response.data.IsActive) {
              UserDataService.setUserData({...response.data,
                id: account.localAccountId});
              setUserDetails({...response.data,
                name: account.name,
                //email: account.idTokenClaims.email,
                id: account.localAccountId,
              });
          } else {
              // Display user-friendly error message using Ant Design Modal
              Modal.error({
                title: 'Authentication Error',
                icon: <ExclamationCircleOutlined />,
                content: response.message || 'You are not allowed to log in.',
                centered: true,
                maskClosable: false,
                okText: 'OK',
                onOk: () => {
                  UserDataService.clearUserData();
                  instance.logoutRedirect();
                }
              });
          }
      } catch (error) {
          console.error(`Error verifying user: ${account.localAccountId}`, error);

          // Check if this is a 404 error (user not found)
          if (error.response?.status === 404 || error.status === 404) {
            Modal.error({
              title: 'User Not Found',
              icon: <ExclamationCircleOutlined />,
              content: error.response.message || error.message || 'Your account was not found in this application. You may be trying to access the wrong application (customer app vs. admin console).',
              centered: true,
              maskClosable: false,
              okText: 'OK',
              onOk: () => {
                UserDataService.clearUserData();
                instance.logoutRedirect();
              }
            });
          } else {
            // Generic error for other cases
            Modal.error({
              title: 'Authentication Error',
              icon: <ExclamationCircleOutlined />,
              content: 'An error occurred while verifying your account. Please try again.',
              centered: true,
              maskClosable: false,
              okText: 'OK',
              onOk: () => {
                UserDataService.clearUserData();
                instance.logoutRedirect();
              }
            });
          }
      }
    };
      //console.log('setting userDetails from account');
      if(account && account.localAccountId){
        console.log("Active account found", account, instance);
        fetchUserDetails();
      }else{
        console.log("No active account found", account, instance);
      }

  },[account, instance, userDetails.id]);

  useEffect(() => {
    let ignore = false;

    const fetchFeatureAccess = async () => {
      const customerId = userDetails?.CustomerID;
      const hasResolvedUser = Boolean(userDetails && Object.keys(userDetails).length > 0);
      if (!customerId) {
        setAssignedPlan(null);
        setEnabledFeatureKeys([]);
        setFeatureAccessLoaded(hasResolvedUser);
        return;
      }

      setFeatureAccessLoaded(false);

      try {
        const assignmentResponse = await execute(
          "GET",
          `${protectedResources.api.baseEndPoint}/ai-subscription/assignments?scopeType=CUSTOMER&scopeId=${customerId}&status=Active`
        );

        if (!assignmentResponse?.success || !Array.isArray(assignmentResponse.data) || assignmentResponse.data.length === 0) {
          if (!ignore) {
            setAssignedPlan(null);
            setEnabledFeatureKeys([]);
          }
          return;
        }

        const now = new Date();
        const currentAssignment = assignmentResponse.data.find((assignment) => {
          const startDate = assignment?.StartDate ? new Date(assignment.StartDate) : null;
          const endDate = assignment?.EndDate ? new Date(assignment.EndDate) : null;
          const hasStarted = !startDate || startDate <= now;
          const notEnded = !endDate || endDate > now;
          return hasStarted && notEnded;
        }) || assignmentResponse.data[0];
        const planResponse = await execute(
          "GET",
          `${protectedResources.api.baseEndPoint}/ai-subscription/plans/${currentAssignment.PlanId}`
        );

        const planDetails = planResponse?.success ? planResponse.data : null;
        const keys = (planDetails?.features || [])
          .filter((feature) => feature.IsEnabled)
          .map((feature) => feature.FeatureKey)
          .filter(Boolean);

        if (!ignore) {
          setAssignedPlan({
            ...currentAssignment,
            PlanCode: planDetails?.PlanCode || currentAssignment.PlanCode,
            PlanName: planDetails?.PlanName || currentAssignment.PlanName
          });
          setEnabledFeatureKeys(keys);
        }
      } catch (error) {
        console.error("Failed to fetch AI feature access:", error);
        if (!ignore) {
          setAssignedPlan(null);
          setEnabledFeatureKeys([]);
        }
      } finally {
        if (!ignore) {
          setFeatureAccessLoaded(true);
        }
      }
    };

    fetchFeatureAccess();

    return () => {
      ignore = true;
    };
  }, [userDetails?.CustomerID]);

  const hasFeatureAccess = React.useCallback((featureKey) => {
    if (!featureKey) return true;
    if (!featureAccessLoaded) return true;
    if (!assignedPlan) return false;
    return enabledFeatureKeys.includes(featureKey);
  }, [featureAccessLoaded, assignedPlan, enabledFeatureKeys]);

  return (
    <UserContext.Provider value={{
      userDetails,
      assignedPlan,
      enabledFeatureKeys,
      featureAccessLoaded,
      hasFeatureAccess
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
